import { IconArrowBack } from "@tine/designsystem-icons/sharp";
import { Grid, Group } from "@tine/designsystem-ui-react";
import { getDate, parseISO } from "date-fns";
import { observer } from "mobx-react-lite";

import type { ContentfulAdventCalendarSlot } from "../../../common/types/cmsTypes";
import { isDateInTheFuture } from "../../../common/utils/dateUtils";
import InformationBox from "../../common/informationBox/InformationBox";
import { InternalLink } from "../../Links";
import ContestForm from "../ContestForm";
import RichTextRenderer from "../RichTextRenderer";
import { AdventCalendarHeader } from "./AdventCalendarHeader";

type AdventCalendarArticleProps = {
   adventCalendarArticleContent: ContentfulAdventCalendarSlot;
   isPreview?: boolean;
};

const calendarFooterImage = new URL("../../../../img/julekalender_footer.png", import.meta.url).toString();

const AdventCalendarArticle = ({ adventCalendarArticleContent, isPreview = false }: AdventCalendarArticleProps) => {
   const prizeImage = adventCalendarArticleContent.fields?.prizeImage;
   const prizeText = adventCalendarArticleContent.fields?.prizeText;
   const title = adventCalendarArticleContent.fields?.title;
   const date = adventCalendarArticleContent.fields.date ? parseISO(adventCalendarArticleContent.fields.date) : undefined;

   return (
      <>
         <Group>
            <AdventCalendarHeader />
         </Group>

         <div className="relative flex justify-center pb-4 sm:pb-8">
            <div className="absolute left-0 top-0">
               <InternalLink href="/julekalender" icon={<IconArrowBack />}>
                  Tilbake til julekalenderen
               </InternalLink>
            </div>
            <h1 className="pt-16 font-suomi text-6xl font-medium sm:pt-0">{date ? `${getDate(date)}. desember` : title}</h1>
         </div>

         <Group>
            {date && isDateInTheFuture(date) && !isPreview && (
               <p>Denne luken er ikke åpen ennå. Gå tilbake til julekalenderen og prøv en annen luke.</p>
            )}
         </Group>

         {((date && !isDateInTheFuture(date)) || isPreview || !date) && (
            <>
               {adventCalendarArticleContent.fields.question && (
                  <Group className="pb-16">
                     <InformationBox theme="christmas" title="Delta i dagens luke og vinn fine premier" className="w-[400px]">
                        <Grid cols={{ base: 12 }} gap={4} className="py-7">
                           <Grid.Col span={{ base: 3 }}>
                              <div className="aspect-square">
                                 {prizeImage ? (
                                    <img
                                       className="h-full w-full rounded-lg object-cover"
                                       src={prizeImage?.fields.file?.url}
                                       alt={prizeText || "Premie"}
                                    />
                                 ) : (
                                    <div className="h-full w-full rounded-lg bg-base-200" />
                                 )}
                              </div>
                           </Grid.Col>
                           <Grid.Col span={{ base: 9 }}>
                              <p className="text-sm">{prizeText}</p>
                           </Grid.Col>
                        </Grid>
                        <ContestForm
                           id={adventCalendarArticleContent.fields.question?.sys.id}
                           entry={adventCalendarArticleContent.fields.question}
                           slug={adventCalendarArticleContent.fields.slug}
                           trackingFormName="Lukepåmelding julekalender"
                        />
                     </InformationBox>
                  </Group>
               )}

               {adventCalendarArticleContent.fields.body && (
                  <div className="pb-12">
                     <RichTextRenderer
                        context="standard-article"
                        document={adventCalendarArticleContent.fields.body}
                        key={JSON.stringify(adventCalendarArticleContent.fields.body)}
                        trackingLocationName="Advent calendar article"
                     />
                  </div>
               )}
               <Group>
                  <img className="h-full w-full" src={calendarFooterImage} alt="Julekalender-footer" />
               </Group>
            </>
         )}
      </>
   );
};

export default observer(AdventCalendarArticle);
